import React, { FC, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Translate } from 'react-localize-redux';
import { PulseLoader } from 'react-spinners';
import { Alert, Button, Col, Row } from 'reactstrap';

import { formatISODate } from '../../../helpers/formatISODate';
import { getFlagEmoji } from '../../../helpers/getFlagEmoji';
import useTranslate from '../../../hooks/useTranslate';
import { useCountries } from '../../../services/apis/countries/api';
import { MappedOrderItemType, MappedOrderItemUserType } from '../../../services/apis/order/types';
import Input from '../../form/Input';
import Select from '../../form/Select';
import {
  isCardNrAlreadyAdded,
  isEmailAlreadyAdded,
  isGolfIdAlreadyAdded,
  validateCardNumber,
  validateCardNumberFormat,
  validateEmail,
  validateGolfId,
} from '../services/validation';
import {
  DefaultUserType,
  FriendType,
  NonSwedenUserType,
  SwedenUserType,
  ThirdPartyUserType,
} from './types';

const SWEDEN_COUNTRY_CODE = 'SE';
const isSwedenCountryCode = (code: string) => code === SWEDEN_COUNTRY_CODE;

interface FieldValues {
  ghCardNr: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  golfId: string;
  email: string;
  handicap: string;
  genderType: string;
}

interface ManualFriendsProps {
  item?: MappedOrderItemType;
  players: MappedOrderItemUserType[];
  isGolfhaftetRequest: boolean;
  isThirdParty: boolean;
  onFriendAddClick: (data: FriendType | null) => void;
  isGitBooking: boolean;
}

export const ManualFriends: FC<ManualFriendsProps> = props => {
  const { item, players, isGolfhaftetRequest, isThirdParty, onFriendAddClick, isGitBooking } =
    props;
  const { translate } = useTranslate();
  const countriesQuery = useCountries();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      ghCardNr: '',
      firstName: '',
      lastName: '',
      countryCode: SWEDEN_COUNTRY_CODE,
      golfId: '',
      email: '',
      handicap: '',
      genderType: '',
    },
  });

  const formValues = watch();
  const { countryCode } = formValues;

  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const validateInputEmail = (value: string): string | undefined => {
    let error = validateEmail(value);
    if (error) {
      return String(translate(error));
    }

    error = isEmailAlreadyAdded(players, value);
    if (error) {
      return String(translate(error));
    }
  };

  const validateInputGolfId = (value: string): string | undefined => {
    let error = validateGolfId(value);
    if (error) {
      return String(translate(error));
    }

    error = isGolfIdAlreadyAdded(players, value);
    if (error) {
      return String(translate(error));
    }
  };

  const validateInputCardNr = (value: string): string | undefined => {
    let error = validateCardNumberFormat(value);
    if (error) {
      return String(translate(error));
    }

    error = isCardNrAlreadyAdded(players, value);
    if (error) {
      return String(translate(error));
    }
  };

  const isValidGolfhaftetCard = async (
    userData: FriendType & { ghCardNr: string }
  ): Promise<boolean> => {
    if (!item) {
      setError(String(translate('default.CartValidationErrors.InternalError')));
      return false;
    }

    setIsValidating(true);
    const error = await validateCardNumber(
      userData.ghCardNr,
      item.golfCourseId,
      formatISODate(new Date(item.startTime))
    );
    setIsValidating(false);
    if (error) {
      setError(String(translate(error)));
      return false;
    }
    return true;
  };

  const mapFriendData = (data: FieldValues): FriendType | null => {
    const { ghCardNr, countryCode, email, firstName, genderType, golfId, handicap, lastName } =
      data;
    let friendData: FriendType | null = null;
    const countryId = countriesQuery.data?.find(({ code }) => code === countryCode)?.id || 0;

    if (isThirdParty) {
      friendData = {
        key: `${firstName}_${lastName}`,
        firstName,
        lastName,
      } satisfies ThirdPartyUserType;
    } else if (isGitBooking) {
      if (isSwedenCountryCode(countryCode)) {
        friendData = {
          key: `${email}_${golfId}`,
          countryId: countryId,
          email,
          golfId,
        } satisfies SwedenUserType;
      } else {
        friendData = {
          key: `${email}_${firstName}_${lastName}`,
          countryId: countryId,
          email,
          firstName,
          lastName,
          genderType,
          handicap,
        } satisfies NonSwedenUserType;
      }
    } else {
      friendData = {
        key: email,
        email,
      } satisfies DefaultUserType;
    }

    if (isGolfhaftetRequest) {
      friendData.ghCardNr = ghCardNr;
    }

    return friendData;
  };

  const onSubmit: SubmitHandler<FieldValues> = async data => {
    setError(null);

    const friendData = mapFriendData(data);

    if (isGolfhaftetRequest && friendData && 'ghCardNr' in friendData) {
      const valid = await isValidGolfhaftetCard(friendData as FriendType & { ghCardNr: string });

      if (!valid) {
        return;
      }
    }

    reset();
    onFriendAddClick(friendData);
  };

  return (
    <>
      <h4 className="mt-3 mb-2 fw-bold">
        {translate(
          isGolfhaftetRequest
            ? 'default.CartAddPlayer.NewPlayerGH'
            : isThirdParty
            ? 'default.CartAddPlayer.NewPlayer'
            : isGitBooking
            ? 'PlayersSelectionComponentAddByEmailOrGolfId'
            : 'PlayersSelectionComponentAddByEmailOrGolfId'
        )}
      </h4>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          {isThirdParty ? (
            <>
              <Col xs="12" sm="6">
                <Input
                  label={String(translate('default.FirstName'))}
                  type="text"
                  name="firstName"
                  disabled={isValidating}
                  register={register}
                  rules={{ required: true }}
                  error={
                    errors.firstName &&
                    translate('default.CartValidationErrors.EmptyPlayerFirstName')
                  }
                />
              </Col>
              <Col xs="12" sm="6">
                <Input
                  label={String(translate('default.LastName'))}
                  type="text"
                  name="lastName"
                  disabled={isValidating}
                  register={register}
                  rules={{ required: true }}
                  error={
                    errors.lastName && translate('default.CartValidationErrors.EmptyPlayerLastName')
                  }
                />
              </Col>
            </>
          ) : isGitBooking ? (
            countriesQuery.isLoading ? (
              <PulseLoader size={10} className={'my-3'} />
            ) : (
              <>
                <Col xs={4}>
                  {countriesQuery.data && (
                    <Select
                      label={String(translate('default.PlayersCountry'))}
                      name="countryCode"
                      showPlaceholder={false}
                      register={register}
                      rules={{ required: true }}
                      options={countriesQuery.data.map(({ code, name }) => ({
                        id: code!,
                        name: `${getFlagEmoji(code!)} ${name}`,
                      }))}
                      error={
                        errors.countryCode && translate('addFriendToBooking.form.errors.required')
                      }
                    />
                  )}
                </Col>
                <Col xs={4}>
                  <Input
                    label={String(translate('default.Email'))}
                    name="email"
                    register={register}
                    rules={{
                      required: isSwedenCountryCode(countryCode)
                        ? false
                        : String(translate('default.CartValidationErrors.EmptyPlayerEmailId')),
                      validate: {
                        invalid: email => (email ? validateInputEmail(email) : undefined),
                      },
                    }}
                    error={errors.email?.message}
                  />
                </Col>
                {isSwedenCountryCode(countryCode) ? (
                  <Col xs={4}>
                    <Input
                      label={String(translate('default.GolfId'))}
                      name="golfId"
                      register={register}
                      rules={{
                        required: String(
                          translate('default.CartValidationErrors.EmptyPlayerGolfId')
                        ),
                        validate: validateInputGolfId,
                      }}
                      error={errors.golfId?.message}
                    />
                  </Col>
                ) : (
                  <>
                    <Col xs={4}>
                      <Input
                        label={String(translate('default.Handicap'))}
                        name="handicap"
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        register={register}
                        rules={{ required: true, min: 0, max: 100 }}
                        error={errors.handicap && translate('errorInvalidHandicap')}
                      />
                    </Col>
                    <Col xs={4}>
                      <Input
                        label={String(translate('default.FirstName'))}
                        name="firstName"
                        register={register}
                        rules={{ required: true }}
                        error={
                          errors.firstName &&
                          translate('default.CartValidationErrors.EmptyPlayerFirstName')
                        }
                      />
                    </Col>
                    <Col xs={4}>
                      <Input
                        label={String(translate('default.LastName'))}
                        name="lastName"
                        disabled={isValidating}
                        register={register}
                        rules={{ required: true }}
                        error={
                          errors.lastName &&
                          translate('default.CartValidationErrors.EmptyPlayerLastName')
                        }
                      />
                    </Col>
                    <Col xs={4}>
                      <Select
                        label={String(translate('default.ChooseGender'))}
                        name="genderType"
                        showPlaceholder={false}
                        register={register}
                        options={[
                          { id: 1, name: String(translate('default.Man')) },
                          { id: 2, name: String(translate('default.Woman')) },
                          { id: 3, name: String(translate('default.GenderNoAnswer')) },
                        ]}
                        rules={{ required: true }}
                        error={errors.genderType && translate('errorInvalidGender')}
                      />
                    </Col>
                  </>
                )}
              </>
            )
          ) : (
            <Col xs="12" sm="8">
              <Input
                label={String(translate('default.Email'))}
                type="text"
                name="email"
                disabled={isValidating}
                register={register}
                rules={{ validate: validateInputEmail }}
                error={errors.email?.message}
              />
            </Col>
          )}
          {isGolfhaftetRequest && (
            <Col xs="12" sm="12">
              <Input
                label={String(translate('default.CartAddPlayer.PlaceholderCardNumber'))}
                type="text"
                name="ghCardNr"
                disabled={isValidating}
                register={register}
                rules={{ validate: validateInputCardNr }}
                error={errors.ghCardNr?.message}
              />
            </Col>
          )}
          <Col xs="12">
            {error && (
              <Alert color="danger" className="p-2">
                {error}
              </Alert>
            )}
            <Button color="success" type="submit" disabled={false}>
              {isValidating && <PulseLoader size={10} />}
              <Translate id="default.Add" />
            </Button>
          </Col>
        </Row>
      </form>
    </>
  );
};
